import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TableSortLabel
} from '@mui/material';
import apiAxiosInstance from '../../utils/axiosInstance';

interface TeacherStat {
  _id: string;
  name: string;
  email: string;
  lessonPlans: number;
  conversations: number;
  assessmentPassages: number;
}

type SortField = 'name' | 'lessonPlans' | 'conversations' | 'assessmentPassages';
type SortDirection = 'asc' | 'desc';

const TeacherStats: React.FC = () => {
  const [stats, setStats] = useState<TeacherStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    fetchTeacherStats();
  }, []);

  const fetchTeacherStats = async () => {
    try {
      setLoading(true);
      const response = await apiAxiosInstance.get('/api/admin/teacher-stats');
      setStats(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedStats = [...stats].sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (sortField) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'lessonPlans':
        aValue = a.lessonPlans;
        bValue = b.lessonPlans;
        break;
      case 'conversations':
        aValue = a.conversations;
        bValue = b.conversations;
        break;
      case 'assessmentPassages':
        aValue = a.assessmentPassages;
        bValue = b.assessmentPassages;
        break;
      default:
        return 0;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    } else {
      return sortDirection === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    }
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Teacher Statistics
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Overview of content created by each teacher
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Teacher Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'lessonPlans'}
                  direction={sortField === 'lessonPlans' ? sortDirection : 'asc'}
                  onClick={() => handleSort('lessonPlans')}
                >
                  Lesson Plans
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'conversations'}
                  direction={sortField === 'conversations' ? sortDirection : 'asc'}
                  onClick={() => handleSort('conversations')}
                >
                  Chat Conversations
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'assessmentPassages'}
                  direction={sortField === 'assessmentPassages' ? sortDirection : 'asc'}
                  onClick={() => handleSort('assessmentPassages')}
                >
                  Assessment Passages
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedStats.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No teachers found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedStats.map((teacher) => (
                <TableRow key={teacher._id} hover>
                  <TableCell>{teacher.name}</TableCell>
                  <TableCell>{teacher.email}</TableCell>
                  <TableCell align="right">{teacher.lessonPlans}</TableCell>
                  <TableCell align="right">{teacher.conversations}</TableCell>
                  <TableCell align="right">{teacher.assessmentPassages}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedStats.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Total Teachers: {sortedStats.length} | 
            Total Lesson Plans: {sortedStats.reduce((sum, t) => sum + t.lessonPlans, 0)} | 
            Total Conversations: {sortedStats.reduce((sum, t) => sum + t.conversations, 0)} | 
            Total Assessment Passages: {sortedStats.reduce((sum, t) => sum + t.assessmentPassages, 0)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TeacherStats;

